import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Loader2 } from 'lucide-react';
import { getApiBase } from '../../apiBase';
import { SKUS } from '../marketingCopy';

const API = axios.create({ baseURL: getApiBase(), timeout: 20000 });

export default function DemoPage({ navigate, mode = 'demo' }) {
  const isTrial = mode === 'trial';
  const skuFromUrl = useMemo(() => {
    try {
      return new URLSearchParams(window.location.search).get('sku') || '';
    } catch {
      return '';
    }
  }, []);

  const [form, setForm] = useState({
    name: '',
    email: '',
    company: '',
    role: '',
    podUrl: '',
    sku: skuFromUrl || 'bundle',
    message: '',
    reason: isTrial ? 'trial' : '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(null);
  const [config, setConfig] = useState(null);

  useEffect(() => {
    API.get('/api/marketing/config', { validateStatus: () => true }).then((res) => {
      if (res.status === 200) setConfig(res.data);
    });
    try {
      const reason = new URLSearchParams(window.location.search).get('reason');
      if (reason) setForm((f) => ({ ...f, reason }));
    } catch {
      // ignore
    }
  }, []);

  function set(field, value) {
    setForm((f) => ({ ...f, [field]: value }));
  }

  async function submit(e) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await API.post('/api/marketing/demo-requests', form, { validateStatus: () => true });
      if (res.status === 201 || res.status === 200) {
        setDone(res.data);
        return;
      }
      setError(res.data?.error || `Could not send request (${res.status})`);
    } catch (err) {
      setError(err.message || 'Network error');
    } finally {
      setLoading(false);
    }
  }

  if (done) {
    return (
      <div className="mx-auto max-w-lg rounded-2xl border border-orca bg-orca-panel p-6">
        <h1 className="text-2xl font-semibold text-orca-strong">
          {isTrial ? 'Trial request received' : 'Request received'}
        </h1>
        <p className="mt-2 text-sm text-slate-300">
          {done.message ||
            (isTrial
              ? 'An SLP architect will follow up to connect your non-prod pod. No admin-approval account is required just to start.'
              : 'An SLP architect will follow up. No admin-approval account is required just to talk.')}
        </p>
        <button type="button" onClick={() => navigate('/demo-tour')} className="mt-4 text-sm text-cyan-400">
          Meanwhile, walk the guided tour →
        </button>
      </div>
    );
  }

  const mailto = config?.contactEmail
    ? `mailto:${config.contactEmail}?subject=${encodeURIComponent(
        isTrial ? 'Orca AI free trial request' : 'Orca AI demo request'
      )}`
    : null;

  return (
    <div className="mx-auto max-w-lg">
      <h1 className="text-3xl font-semibold text-orca-strong">
        {isTrial ? 'Get Orca on your pod' : 'See it on your pod'}
      </h1>
      <p className="mt-2 text-sm text-slate-400">
        {isTrial
          ? 'Request a trial on your non-prod Fusion pod (or an SLP sandbox). We connect Security Copilot, User Lookup, SoD, and Access Review in the browser — no desktop install.'
          : 'Tell us about your Fusion pod. We will not ask you to wait on an SLP account approval just to evaluate the product story.'}
      </p>
      {form.reason === 'password' && (
        <p className="mt-3 rounded-lg border border-orca bg-orca-raised p-3 text-sm text-slate-300">
          Password reset is handled by an SLP administrator. Use this form and we will reach you.
        </p>
      )}
      <form onSubmit={submit} className="mt-6 space-y-3">
        <Field label="Name" value={form.name} onChange={(v) => set('name', v)} required />
        <Field label="Work email" type="email" value={form.email} onChange={(v) => set('email', v)} required />
        <Field label="Company" value={form.company} onChange={(v) => set('company', v)} required />
        <Field label="Role" value={form.role} onChange={(v) => set('role', v)} placeholder="HCM security, HRIS, audit…" />
        <Field
          label="Fusion host (optional)"
          value={form.podUrl}
          onChange={(v) => set('podUrl', v)}
          placeholder="https://xxx.fa.us6.oraclecloud.com"
        />
        <label className="block text-xs font-medium text-orca-muted">
          SKU interest
          <select
            value={form.sku}
            onChange={(e) => set('sku', e.target.value)}
            className="mt-1 w-full rounded-lg border border-orca px-3 py-2 text-sm"
          >
            {SKUS.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </label>
        <label className="block text-xs font-medium text-orca-muted">
          What do you want to see?
          <textarea
            value={form.message}
            onChange={(e) => set('message', e.target.value)}
            rows={4}
            className="mt-1 w-full rounded-lg border border-orca px-3 py-2 text-sm"
          />
        </label>
        {error && <p className="text-sm text-rose-300">{error}</p>}
        <button
          type="submit"
          disabled={loading}
          className="flex w-full items-center justify-center gap-2 rounded-lg bg-orca-volt py-2.5 text-sm font-semibold hover:brightness-110 disabled:opacity-60"
        >
          {loading && <Loader2 className="h-4 w-4 animate-spin" />}
          {isTrial ? 'Request free trial' : 'Send request'}
        </button>
      </form>
      {mailto && (
        <p className="mt-4 text-center text-xs text-slate-500">
          Or email{' '}
          <a className="text-cyan-400" href={mailto}>
            {config.contactEmail}
          </a>
        </p>
      )}
    </div>
  );
}

function Field({ label, value, onChange, type = 'text', required, placeholder }) {
  return (
    <label className="block text-xs font-medium text-orca-muted">
      {label}
      <input
        type={type}
        value={value}
        required={required}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full rounded-lg border border-orca px-3 py-2 text-sm"
      />
    </label>
  );
}
